import React from "react";
import clsx from "clsx";

const VARIANTS = {
  desktop: {
    container: "bg-violet-500/10 border border-violet-500/20 p-3",
    badgeHigh: "bg-green-500/20 text-green-300",
    badgeMed: "bg-yellow-500/20 text-yellow-300",
    badgeLow: "bg-red-500/20 text-red-300",
    analyze: "text-violet-400 hover:text-violet-300",
    winnerText: "text-sm",
    reasonText: "text-xs text-white/70",
  },
  mobile: {
    container: "bg-blue-500/5 border border-blue-500/30 p-2",
    badgeHigh: "bg-green-500/20 text-green-300",
    badgeMed: "bg-yellow-500/20 text-yellow-300",
    badgeLow: "bg-red-500/20 text-red-300",
    analyze: "text-violet-400 hover:text-violet-300",
    winnerText: "text-sm",
    reasonText: "text-[10px] text-white/70",
  },
};

/**
 * Shared prediction box used across dashboards.
 * Keeps display logic for winner, confidence badge and reasoning in one place.
 */
const PredictionBox = ({
  prediction,
  onAnalyze,
  analyzeLabel = "Analyze in Chat →",
  variant = "desktop",
  className = "",
  t = (text) => text,
}) => {
  if (!prediction) return null;

  const styles = VARIANTS[variant] || VARIANTS.desktop;

  const confidenceClass =
    prediction.confidence === "HIGH"
      ? styles.badgeHigh
      : prediction.confidence === "MED"
      ? styles.badgeMed
      : styles.badgeLow;

  const handleAnalyze = () => {
    if (onAnalyze) onAnalyze();
  };

  const isDetailedPrediction =
    typeof prediction === "object" &&
    (prediction.winner || prediction.confidence || prediction.reason);

  return (
    <div
      className={clsx(
        "rounded-lg text-white",
        styles.container,
        className
      )}
    >
      <div className={styles.winnerText}>
        {isDetailedPrediction ? (
          <>
            <div className="flex items-center gap-2 mb-2">
              <span className="font-bold flex-1">
                {prediction.winner}
                {prediction.confidenceNumeric && (
                  <span className="text-white/80 font-normal ml-1.5">
                    ({prediction.confidenceNumeric}%)
                  </span>
                )}
              </span>
              {prediction.confidence && (
                <span
                  className={clsx(
                    "px-2.5 py-0.5 rounded text-xs font-bold",
                    confidenceClass
                  )}
                >
                  {prediction.confidence}
                </span>
              )}
            </div>
            {prediction.reason && (
              <p className={clsx("leading-relaxed", styles.reasonText)}>
                {prediction.reason}
              </p>
            )}
          </>
        ) : (
          <div className="text-xs text-white/70">{prediction}</div>
        )}
      </div>

      {onAnalyze && (
        <button
          onClick={handleAnalyze}
          className={clsx(
            "text-xs hover:underline mt-2 inline-block transition-colors cursor-pointer bg-transparent border-none p-0",
            styles.analyze
          )}
        >
          {t(analyzeLabel)}
        </button>
      )}

      {prediction.cached && (
        <div className="text-xs text-yellow-400 mt-1 font-semibold">
          Cached • {prediction.cooldownRemaining}m remaining
        </div>
      )}
    </div>
  );
};

export default PredictionBox;
